import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  MessageSquare, 
  Eye, 
  Trash2, 
  Search, 
  Filter, 
  CheckCircle2, 
  Clock, 
  FileText, 
  ExternalLink, 
  User, 
  Mail, 
  Phone, 
  RefreshCw,
  AlertCircle,
  Bot
} from 'lucide-react';
import Modal from '../components/common/Modal';
import NotificationSystem, { useNotification } from '../components/common/NotificationSystem';
import CustomSelect from '../components/common/CustomSelect';
import chatSessionService from '../services/chatSessionService';

const ChatSessionListPage = () => {
  const navigate = useNavigate();
  const { notification, showNotification, hideNotification } = useNotification();

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  // Modal para ver conversación completa
  const [selectedSession, setSelectedSession] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  // Modal para eliminar
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const loadSessions = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const data = await chatSessionService.getChatSessions({
        page,
        limit: itemsPerPage,
        search: searchTerm,
        status: statusFilter === 'ALL' ? '' : statusFilter
      });
      setSessions(data.sessions || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setTotalItems(data.pagination?.totalItems || 0);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error cargando sesiones de chat:', error);
      showNotification(error.message || 'Error al cargar las consultas de chat', 'error');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter, itemsPerPage, showNotification]);

  useEffect(() => {
    loadSessions(1);
  }, [loadSessions]);

  const handleOpenDetail = async (sessionId) => {
    try {
      setShowDetailModal(true);
      setDetailLoading(true);
      const data = await chatSessionService.getChatSessionById(sessionId);
      setSelectedSession(data.session);
    } catch (error) {
      showNotification('Error al cargar la transcripción del chat', 'error');
      setShowDetailModal(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const confirmDelete = (session) => {
    setSessionToDelete(session);
    setShowDeleteModal(true);
  };

  const executeDelete = async () => {
    if (!sessionToDelete) return;
    try {
      await chatSessionService.deleteChatSession(sessionToDelete.id);
      showNotification('Sesión de chat eliminada exitosamente', 'success');
      setShowDeleteModal(false);
      setSessionToDelete(null);
      loadSessions(currentPage);
    } catch (error) {
      showNotification(error.message || 'Error al eliminar la sesión', 'error');
    }
  };

  const getStatusBadge = (session) => {
    if (session.status === 'CONVERTED_TO_PROPOSAL' || session.proposal) {
      return (
        <span className="badge badge-success" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <FileText size={12} />
          {session.proposal?.code || 'Propuesta Generada'}
        </span>
      );
    }
    if (session.status === 'PROSPECT') {
      return (
        <span className="badge badge-warning" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <User size={12} />
          Prospecto Identificado
        </span>
      );
    }
    return (
      <span className="badge badge-info" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        <MessageSquare size={12} />
        Consulta General
      </span>
    );
  };

  return (
    <div className="animate-fadeIn">
      {/* Encabezado */}
      <div className="page-header flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1 flex items-center gap-2">
            <span>💬</span> Atención al Cliente • Consultas Web
          </h1>
          <p className="text-gray-600 text-sm">
            Monitoreo en vivo de conversaciones con Xtreme-AI desde la landing page
          </p>
        </div>
        <button onClick={() => loadSessions(currentPage)} className="btn btn-outline flex items-center gap-1.5" title="Actualizar lista">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          <span>Actualizar</span>
        </button>
      </div>

      {/* Tarjeta con Filtros y Tabla */}
      <div className="card">
        {/* Barra de Búsqueda y Filtros */}
        <div className="card-header pb-4 border-b border-gray-200">
          <div className="flex flex-col md:flex-row gap-4 justify-between">
            {/* Buscador */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Buscar por nombre, correo, WhatsApp o tema..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-10 w-full"
              />
            </div>

            {/* Filtro por Estado */}
            <div className="w-full md:w-64">
              <CustomSelect
                value={statusFilter}
                onChange={(val) => setStatusFilter(val)}
                options={[
                  { value: 'ALL', label: 'Todas las consultas' },
                  { value: 'INQUIRY', label: '💬 Consultas Generales' },
                  { value: 'PROSPECT', label: '👤 Con Datos de Contacto' },
                  { value: 'CONVERTED_TO_PROPOSAL', label: '📝 Convertidas a Propuesta' }
                ]}
              />
            </div>
          </div>
        </div>

        {/* Tabla de Sesiones */}
        <div className="card-body p-0">
          {loading ? (
            <div className="p-12 text-center text-gray-500">
              <RefreshCw size={32} className="animate-spin mx-auto mb-3 text-primary-500" />
              <p>Cargando historial de consultas web...</p>
            </div>
          ) : sessions.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <MessageSquare size={48} className="mx-auto mb-3 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-800 mb-1">No hay consultas de chat registradas</h3>
              <p className="text-sm text-gray-500">
                {searchTerm || statusFilter !== 'ALL'
                  ? 'No se encontraron resultados para los filtros aplicados.'
                  : 'Las interacciones de los visitantes con Xtreme-AI en la web aparecerán aquí.'}
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Fecha / Hora</th>
                    <th>Visitante / Contacto</th>
                    <th>Tema / Requerimiento</th>
                    <th style={{ textAlign: 'center' }}>Mensajes</th>
                    <th>Estado</th>
                    <th style={{ textAlign: 'right' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s) => (
                    <tr key={s.id} className="hover:bg-gray-50/60 transition-colors">
                      {/* Fecha */}
                      <td className="whitespace-nowrap text-sm text-gray-600">
                        <div className="flex items-center gap-1.5 font-mono text-xs">
                          <Clock size={13} className="text-gray-400" />
                          <span>{new Date(s.updatedAt || s.createdAt).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' })}</span>
                        </div>
                      </td>

                      {/* Visitante */}
                      <td>
                        <div className="text-sm">
                          <p className="font-semibold text-gray-900 flex items-center gap-1.5">
                            <User size={14} className="text-gray-400" />
                            <span>{s.visitor.name || 'Visitante Web'}</span>
                          </p>
                          <div className="flex flex-wrap gap-2 mt-0.5 text-xs text-gray-500">
                            {s.visitor.phone && (
                              <span className="flex items-center gap-1 font-mono text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                                <Phone size={11} /> {s.visitor.phone}
                              </span>
                            )}
                            {s.visitor.email && (
                              <span className="flex items-center gap-1 text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">
                                <Mail size={11} /> {s.visitor.email}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Tema Principal */}
                      <td className="max-w-[240px]">
                        <p className="text-sm font-medium text-gray-800 truncate" title={s.lastTopic}>
                          {s.lastTopic || 'Consulta General'}
                        </p>
                        {s.lastMessage && (
                          <p className="text-xs text-gray-400 truncate mt-0.5" title={s.lastMessage}>
                            {s.lastMessage}
                          </p>
                        )}
                      </td>

                      {/* Total Mensajes */}
                      <td style={{ textAlign: 'center' }}>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                          {s.messageCount || 1}
                        </span>
                      </td>

                      {/* Estado */}
                      <td>
                        {getStatusBadge(s)}
                      </td>

                      {/* Acciones */}
                      <td style={{ textAlign: 'right' }}>
                        <div className="flex items-center justify-end gap-2">
                          {/* Ver Transcripción */}
                          <button
                            onClick={() => handleOpenDetail(s.id)}
                            className="btn btn-primary btn-sm"
                            title="Ver conversación completa"
                          >
                            <Eye size={16} />
                            <span>Ver Chat</span>
                          </button>

                          {/* Enlace a Propuesta si existe */}
                          {s.proposal && (
                            <Link
                              to={`/proposals/${s.proposal.id}`}
                              className="btn btn-info btn-sm no-underline"
                              title="Ir a Propuesta Comercial"
                            >
                              <FileText size={16} />
                              <span>Propuesta</span>
                            </Link>
                          )}

                          {/* Eliminar */}
                          <button
                            onClick={() => confirmDelete(s)}
                            className="btn btn-danger btn-sm"
                            title="Eliminar sesión"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Paginador */}
        {totalPages > 1 && (
          <div className="card-footer flex justify-between items-center border-t border-gray-200 px-6 py-4">
            <span className="text-xs text-gray-500">
              Mostrando página {currentPage} de {totalPages} ({totalItems} consultas en total)
            </span>
            <div className="flex gap-1.5">
              <button
                onClick={() => loadSessions(currentPage - 1)}
                disabled={currentPage === 1}
                className="btn btn-sm btn-outline text-xs disabled:opacity-40"
              >
                Anterior
              </button>
              <button
                onClick={() => loadSessions(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="btn btn-sm btn-outline text-xs disabled:opacity-40"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {/* MODAL DE TRANSCRIPCIÓN DE LA CONVERSACIÓN - MODO CHAT */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedSession(null);
        }}
        title="Transcripción de la Conversación Web"
        size="default"
        hideSubmit={true}
        cancelText="Cerrar"
      >
        {detailLoading || !selectedSession ? (
          <div className="p-12 text-center text-gray-500">
            <RefreshCw size={28} className="animate-spin mx-auto mb-3 text-emerald-600" />
            <p className="text-sm font-medium">Cargando mensajes del chat...</p>
          </div>
        ) : (
          <div className="chat-modal-body">
            {/* Header del Visitante */}
            <div className="chat-visitor-header">
              <div className="chat-visitor-info">
                <div className="chat-visitor-avatar">
                  {selectedSession.visitor?.name ? selectedSession.visitor.name.charAt(0).toUpperCase() : <User size={20} />}
                </div>
                <div className="chat-visitor-details">
                  <div className="chat-visitor-name">
                    {selectedSession.visitor?.name || 'Visitante Web'}
                  </div>
                  <div className="chat-visitor-meta">
                    {selectedSession.visitor?.phone ? (
                      <span className="chat-meta-tag chat-meta-phone">
                        <Phone size={11} /> {selectedSession.visitor.phone}
                      </span>
                    ) : (
                      <span>Sin WhatsApp</span>
                    )}
                    <span>•</span>
                    {selectedSession.visitor?.email ? (
                      <span className="chat-meta-tag chat-meta-email">
                        <Mail size={11} /> {selectedSession.visitor.email}
                      </span>
                    ) : (
                      <span>Sin correo registrado</span>
                    )}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {getStatusBadge(selectedSession)}
                {selectedSession.proposal && (
                  <Link
                    to={`/proposals/${selectedSession.proposal.id}`}
                    className="btn btn-info btn-sm no-underline"
                    title="Abrir Propuesta Comercial"
                  >
                    <FileText size={14} />
                    <span>Propuesta #{selectedSession.proposal.code}</span>
                    <ExternalLink size={12} />
                  </Link>
                )}
              </div>
            </div>

            {/* Contenedor Modo Chat Real */}
            <div className="chat-messages-box">
              {/* Fecha de la sesión centrada */}
              <div className="chat-date-pill">
                📅 Conversación iniciada el {new Date(selectedSession.createdAt).toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </div>

              {selectedSession.messages && selectedSession.messages.length > 0 ? (
                selectedSession.messages.map((m, idx) => {
                  const isUser = m.role === 'user';
                  const isAssistant = m.role === 'assistant';
                  const timeStr = m.timestamp
                    ? new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : '';

                  if (!isUser && !isAssistant) {
                    return (
                      <div key={idx} style={{ textAlign: 'center', margin: '4px 0' }}>
                        <span style={{ fontSize: '11px', backgroundColor: '#e2e8f0', color: '#475569', padding: '3px 10px', borderRadius: '12px' }}>
                          {m.content}
                        </span>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={idx}
                      className={`chat-msg-row ${isUser ? 'user' : 'assistant'}`}
                    >
                      {/* Avatar Asistente (izquierda) */}
                      {!isUser && (
                        <div className="chat-msg-avatar assistant" title="Xtreme-AI">
                          <Bot size={18} />
                        </div>
                      )}

                      {/* Burbuja del Mensaje */}
                      <div className={`chat-msg-bubble ${isUser ? 'user' : 'assistant'}`}>
                        {/* Cabecera del Mensaje */}
                        <div className={`chat-msg-header ${isUser ? 'user' : 'assistant'}`}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {isUser ? (
                              <>
                                <User size={12} />
                                <span>{selectedSession.visitor?.name || 'Visitante Web'}</span>
                              </>
                            ) : (
                              <>
                                <Bot size={12} />
                                <span>Xtreme-AI</span>
                                <span className="chat-msg-badge">Consultor IA</span>
                              </>
                            )}
                          </span>
                        </div>

                        {/* Contenido del Mensaje */}
                        <p className="chat-msg-content">
                          {m.content}
                        </p>

                        {/* Timestamp */}
                        <div className="chat-msg-time">
                          {timeStr}
                        </div>
                      </div>

                      {/* Avatar Visitante (derecha) */}
                      {isUser && (
                        <div className="chat-msg-avatar user" title={selectedSession.visitor?.name || 'Visitante Web'}>
                          <User size={16} />
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div style={{ padding: '32px', textAlign: 'center', color: '#94a3b8' }}>
                  <MessageSquare size={32} style={{ margin: '0 auto 8px', color: '#cbd5e1' }} />
                  <p style={{ fontSize: '13px', margin: 0 }}>No hay mensajes registrados en esta sesión.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* MODAL DE CONFIRMACIÓN PARA ELIMINAR */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSessionToDelete(null);
        }}
        title="Eliminar Sesión de Chat"
        size="small"
        onSubmit={(e) => { e.preventDefault(); executeDelete(); }}
        submitText="Eliminar Definitivamente"
        submitButtonClass="btn-danger"
        cancelText="Cancelar"
      >
        <div className="p-4 bg-red-50 text-red-700 rounded-lg border border-red-200 flex items-start gap-3">
          <AlertCircle size={24} className="shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-sm mb-1">¿Eliminar esta conversación?</p>
            <p className="text-xs text-red-600">
              Esta acción eliminará el registro de chat de{' '}
              <strong>{sessionToDelete?.visitor?.name || 'Visitante Web'}</strong> permanentemente.
            </p>
          </div>
        </div>
      </Modal>

      {/* Notificaciones */}
      <NotificationSystem notification={notification} onClose={hideNotification} />
    </div>
  );
};

export default ChatSessionListPage;
